import {
	CheckCircleOutlined,
	ClockCircleOutlined,
	PaperClipOutlined,
	StarOutlined,
	WarningOutlined,
} from "@ant-design/icons";
import { Button, Tag, Tooltip } from "antd";
import { Dayjs } from "dayjs";
import { ReactNode, useId, useState } from "react";
import { formatDateDdMmmYyyy } from "../../../utils/date_formatter";
import {
	FINDING_STATUS_LABELS,
	FindingStage,
	FindingStageReport,
	FindingStatus,
	FindingTiming,
	formatDayCount,
	getFindingPerson,
	getFindingStages,
	getFindingStatus,
	getFindingTypeLabel,
} from "../../../utils/reporting/findingFilters";
import {
	getReportDocumentName,
	getShownReportDocuments,
} from "../../../utils/reporting/reportFiles";
import { ReportItemKind } from "../../../utils/reporting/reportItemKinds";
import { getInitials } from "../../../utils/utils";

// An item as the report's page shows it: a finding or a follow-up action.
export type ReportItemEntry = {
	item: any;
	// Its position in the report, which numbers it and opens it in the drawer.
	index: number;
	timing: FindingTiming;
};

// What the cards and the list both take.
export type ReportItemViewProps = {
	kind: ReportItemKind;
	entries: ReportItemEntry[];
	// Off until the report goes out for action: items have no real status
	// before then.
	showStatus: boolean;
	// Off when the items have fewer than two types between them.
	showType?: boolean;
	// The report's state and dates, which each item's stages read.
	stageReport: FindingStageReport;
	// With both set, each item the user can work on opens in the report's
	// drawer: by clicking its card, or with its row's edit button.
	canEditItem?: (item: any) => boolean;
	onEditItem?: (index: number) => void;
	// Opens doc in the preview, among the item's other documents.
	onOpenDocument?: (doc: any, documents: any[]) => void;
};

const STATUS_TAGS: Record<FindingStatus, { color: string; icon: ReactNode }> = {
	pending: { color: "default", icon: <StarOutlined /> },
	draft: { color: "orange", icon: <ClockCircleOutlined /> },
	completed: { color: "green", icon: <CheckCircleOutlined /> },
};

// An item's workflow status, the same in the drawer and on the report's page.
export function ReportItemStatusTag({
	status,
}: Readonly<{ status: FindingStatus }>) {
	const { color, icon } = STATUS_TAGS[status];
	return (
		<Tag color={color} icon={icon}>
			{FINDING_STATUS_LABELS[status]}
		</Tag>
	);
}

const CRITICALITY_TAGS: Record<string, { label: string; color: string }> = {
	high: { label: "High criticality", color: "red" },
	medium: { label: "Medium criticality", color: "gold" },
	low: { label: "Low criticality", color: "default" },
};

const isAlreadyCompleted = (item: any) => item?.already_completed === true;

// The documents attached to an item, as getShownReportDocuments lists them.
export const getReportItemDocuments = (item: any): any[] =>
	getShownReportDocuments(item?.documents);

const isNotStoredInSharePoint = (doc: any) => doc?.upload_status === "failed";

const notStoredMessage = (doc: any) => {
	const reason = doc?.upload_error?.userMessage;
	return reason
		? `Not stored in SharePoint: ${reason}`
		: "Not stored in SharePoint.";
};

const formatDay = (date: Dayjs) => formatDateDdMmmYyyy(date.toISOString());

// Type (unless left out), criticality (an audit's Non-Conformity findings
// only), completed before any follow-up and, where the layout has no room for
// the files themselves, how many there are.
export function ReportItemTags({
	item,
	kind,
	attachmentCount = 0,
	showType = true,
}: Readonly<{
	item: any;
	kind: ReportItemKind;
	attachmentCount?: number;
	showType?: boolean;
}>) {
	const type = item?.[kind.typeField];
	// Only text is shown as a type: nothing checks what else could be stored.
	const hasType = typeof type === "string" && type !== "";
	const criticality =
		kind.hasCriticality && type === "Non-Conformity"
			? CRITICALITY_TAGS[item?.criticality]
			: undefined;

	return (
		<span className="report-items__tags">
			{showType && hasType && <Tag>{getFindingTypeLabel(type, kind)}</Tag>}
			{criticality && <Tag color={criticality.color}>{criticality.label}</Tag>}
			{isAlreadyCompleted(item) && <Tag>{kind.alreadyCompletedTag}</Tag>}
			{attachmentCount > 0 && (
				<span className="report-items__attachment-count">
					<PaperClipOutlined />
					{attachmentCount} {attachmentCount === 1 ? "file" : "files"}
				</span>
			)}
		</span>
	);
}

// The person an item shows: whoever completed it once that is recorded,
// otherwise whoever is responsible. By name, with the email on hover.
export function ReportItemPerson({ item }: Readonly<{ item: any }>) {
	const { label, email, name } = getFindingPerson(item);
	if (label === "Responsible" && isAlreadyCompleted(item)) {
		return <span className="report-items__muted">No follow-up</span>;
	}
	if (!name) return <span className="report-items__muted">—</span>;

	return (
		<Tooltip title={name === email ? undefined : email}>
			<span className="report-items__person">
				<span className="report-items__avatar" aria-hidden="true">
					{getInitials(name, 2)}
				</span>
				<span className="report-items__person-name">{name}</span>
			</span>
		</Tooltip>
	);
}

// The line under an open item's deadline: how it stands against it. A
// completed one's stages say when it was done.
const describeTiming = (
	timing: FindingTiming
): { text: string; modifier?: "overdue" } | null => {
	switch (timing.kind) {
		case "overdue":
			return {
				text: `${formatDayCount(timing.days)} overdue`,
				modifier: "overdue",
			};
		case "due":
			return {
				text:
					timing.days === 0
						? "Due today"
						: `Due in ${formatDayCount(timing.days)}`,
			};
		default:
			return null;
	}
};

export function ReportItemDeadline({
	entry,
}: Readonly<{ entry: ReportItemEntry }>) {
	const { item, timing } = entry;
	if (isAlreadyCompleted(item)) {
		return <span className="report-items__muted">—</span>;
	}

	const line = describeTiming(timing);
	const isOverdue = timing.kind === "overdue";

	return (
		<span className="report-items__deadline">
			<span className={isOverdue ? "report-items__overdue-date" : undefined}>
				{formatDateDdMmmYyyy(item?.deadline)}
			</span>
			{line && (
				<span
					className={
						line.modifier
							? `report-items__timing report-items__timing--${line.modifier}`
							: "report-items__timing"
					}
				>
					{isOverdue && <WarningOutlined />}
					{line.text}
				</span>
			)}
		</span>
	);
}

// An audit finding's recommended action. Kinds without one leave the section
// out.
export function ReportItemRecommendedAction({
	text,
}: Readonly<{ text?: string }>) {
	return (
		<div className="report-items__field">
			<span className="report-items__label">Recommended action</span>
			{text ? (
				<p className="report-items__text">{text}</p>
			) : (
				<p className="report-items__muted">None recorded</p>
			)}
		</div>
	);
}

// The outcome, such as "What was done", once an item is completed. Before
// that, while the report is out for action, a note that it is still to come.
// When and by whom show in the item's stages and person.
export function ReportItemOutcome({
	entry,
	kind,
	showStatus,
	className,
}: Readonly<{
	entry: ReportItemEntry;
	kind: ReportItemKind;
	showStatus: boolean;
	className?: string;
}>) {
	const { item } = entry;
	const classes = (modifier: string) =>
		[`report-items__outcome report-items__outcome--${modifier}`, className]
			.filter(Boolean)
			.join(" ");

	if (getFindingStatus(item) !== "completed") {
		if (!showStatus) return null;
		return (
			<div className={classes("pending")}>
				<span className="report-items__label">{kind.outcomeLabel}</span>
				<p className="report-items__text">
					{`Not completed yet. Added when the ${kind.noun} is marked complete.`}
				</p>
			</div>
		);
	}

	const outcome = item?.[kind.outcomeField] || "—"; // NOSONAR: empty text shows a dash
	return (
		<div className={classes("done")}>
			<span className="report-items__outcome-title">
				<CheckCircleOutlined />
				{kind.outcomeLabel}
			</span>
			<p className="report-items__text">{outcome}</p>
		</div>
	);
}

const STAGE_NAMES: Record<FindingStage["key"], string> = {
	created: "Created",
	reviewed: "Reviewed",
	completed: "Completed",
};

const STAGE_STATES: Record<FindingStage["state"], string> = {
	done: "done",
	current: "current",
	todo: "not yet",
};

// How many stages are done before the first one that isn't, which is how far
// the line between the dots is filled.
const countReached = (stages: FindingStage[]) => {
	const firstNotDone = stages.findIndex((stage) => stage.state !== "done");
	return firstNotDone === -1 ? stages.length : firstNotDone;
};

// An item's stages, Created, Reviewed and Completed, as a small tracker: a
// dot for each on a line, and the date and note of each one done.
export function ReportItemStages({
	item,
	report,
	kind,
}: Readonly<{ item: any; report: FindingStageReport; kind: ReportItemKind }>) {
	const stages = getFindingStages(item, report, kind);

	return (
		<div
			className={`report-items__stages report-items__stages--reached-${countReached(stages)}`}
		>
			<ol className="report-items__stage-list" aria-label="Stages">
				{stages.map(({ key, state, date, note }) => (
					<li
						key={key}
						className={`report-items__stage report-items__stage--${state}`}
					>
						<span className="report-items__stage-dot" aria-hidden="true" />
						<span className="report-items__stage-name">
							{STAGE_NAMES[key]}
							<span className="sr-only">, {STAGE_STATES[state]}</span>
						</span>
						{date && (
							<span className="report-items__stage-detail">
								{formatDay(date)}
							</span>
						)}
						{note && (
							<span
								className={
									note.modifier
										? `report-items__stage-detail report-items__stage-detail--${note.modifier}`
										: "report-items__stage-detail"
								}
							>
								{note.text}
							</span>
						)}
					</li>
				))}
			</ol>
		</div>
	);
}

// An item's attachments as buttons that open them. With a limit, a long list
// shows that many and a button for the rest, as a card's description shows
// "Read more": one more file than the limit is shown rather than hidden, since
// "+1 more" would take as much room as the file.
export function ReportItemAttachments({
	documents,
	onOpenDocument,
	limit,
}: Readonly<{
	documents: any[];
	onOpenDocument?: (doc: any, documents: any[]) => void;
	limit?: number;
}>) {
	const listId = useId();
	const [expanded, setExpanded] = useState(false);

	if (documents.length === 0) {
		return <p className="report-items__muted">No attachments</p>;
	}

	const collapsible = limit !== undefined && documents.length > limit + 1;
	const shown =
		collapsible && !expanded ? documents.slice(0, limit) : documents;
	const hiddenCount = documents.length - shown.length;

	return (
		<div className="report-items__attachments">
			<ul id={listId} className="report-items__files" aria-label="Attachments">
				{shown.map((doc) => {
					const name = getReportDocumentName(doc);
					const notStored = isNotStoredInSharePoint(doc);
					const button = (
						<Button
							size="small"
							danger={notStored}
							icon={notStored ? <WarningOutlined /> : <PaperClipOutlined />}
							title={notStored ? undefined : name}
							disabled={!onOpenDocument}
							onClick={() => onOpenDocument?.(doc, documents)}
						>
							{name}
							{notStored && (
								<span className="sr-only"> (not stored in SharePoint)</span>
							)}
						</Button>
					);
					return (
						<li key={doc.uid ?? doc.sharepoint_id ?? doc.file_name ?? name}>
							{notStored ? (
								<Tooltip title={notStoredMessage(doc)}>{button}</Tooltip>
							) : (
								button
							)}
						</li>
					);
				})}
			</ul>
			{collapsible && (
				<button
					type="button"
					className="report-items__more"
					aria-expanded={expanded}
					aria-controls={listId}
					onClick={() => setExpanded((value) => !value)}
				>
					{expanded ? (
						"Show fewer"
					) : (
						<>
							{`+${hiddenCount} more`}
							<span className="sr-only"> attachments</span>
						</>
					)}
				</button>
			)}
		</div>
	);
}
